import { CheckCircle2, Clock3, OctagonAlert } from "lucide-react";
import { formatDateTime } from "../app-bindings/attachment-state-ui";
import type {
  AsynchronousExecutionKind,
  AsynchronousExecutionState,
  AsynchronousExecutionResponse,
} from "../app-bindings/hooks/useAppBindings";

export function asynchronousExecutionKindLabel(kind: AsynchronousExecutionKind) {
  const labels: Record<AsynchronousExecutionKind, string> = {
    attachment_storage_alignment: "添付ファイル整合化",
    attachment_recovery_export: "復元情報書き出し",
    attachment_environment_restore_preview: "環境移行復元の内容確認",
    attachment_environment_restore: "添付ファイル環境復元",
    orphan_record_folder_cleanup: "孤立レコードフォルダ整理",
  };
  return labels[kind];
}

export function asynchronousExecutionStateLabel(
  state: AsynchronousExecutionState,
  startedAt: string | null | undefined = null,
) {
  const labels: Record<AsynchronousExecutionState, string> = {
    queued: startedAt === null || startedAt === undefined
      ? "開始待ち"
      : "次工程の開始待ち",
    running: "実行中",
    blocked: "要対応",
    cancel_requested: "取消処理中",
    completed: "完了",
    failed: "失敗",
    canceled: "取消済み",
  };
  return labels[state];
}

export function asynchronousExecutionReasonLabel(reasonCode: string | null) {
  if (reasonCode === null) return "-";
  if (reasonCode === "settings_changed_by_actor") return "設定変更による中止";
  if (reasonCode === "manual_repair_required") {
    return "自動処理を継続できませんでした（manual_repair_required）";
  }
  if (reasonCode === "referenced_file_missing") return "参照ファイルが存在しないため復旧を停止しました。";
  if (reasonCode === "source_precondition_changed") {
    return "開始時の前提条件と現在の状態が一致しません（source_precondition_changed）";
  }
  const labels: Record<string, string> = {
    alignment_capacity_wait: "今回の処理枠を終えたため続行を待っています",
    alignment_start_deadline_wait: "新しい処理の開始枠を終えたため続行を待っています",
    alignment_connection_wait: "転送前の接続が成立しなかったため待機しています",
    dependency_unavailable: "依存先にアクセスできません（dependency_unavailable）",
    authorization_revoked: "処理に必要な権限を確認できません（authorization_revoked）",
    pf_response_unknown: "処理結果を確認できません（pf_response_unknown）",
    pf_identity_conflict: "処理の対象または操作IDが一致しません（pf_identity_conflict）",
    deadline_exceeded: "実行期限を超えました（deadline_exceeded）",
    manifest_missing: "処理記録を取得できません（manifest_missing）",
    manifest_digest_mismatch: "処理記録の内容が一致しません（manifest_digest_mismatch）",
    manifest_expired_rescan_required: "処理記録の保持期限を過ぎています（manifest_expired_rescan_required）",
  };
  return labels[reasonCode] ?? reasonCode;
}

/** Validate the new response field at the display boundary; never turn missing/invalid values into zero. */
export function attachmentTransferProgressText(progress: unknown): string {
  if (progress === null) return "対象外";
  if (!progress || typeof progress !== "object" || Array.isArray(progress)) return "未確認";
  const { fileCount, sizeBytes } = progress as Record<string, unknown>;
  const decimal = (value: unknown): value is string => typeof value === "string" && /^(0|[1-9][0-9]{0,18})$/u.test(value);
  if (!decimal(fileCount) || !decimal(sizeBytes)) return "未確認";
  const files = BigInt(fileCount), bytes = BigInt(sizeBytes), maximum = 9223372036854775807n;
  if (files > maximum || bytes > maximum || files === 0n && bytes !== 0n) return "未確認";
  return `${files.toLocaleString()}ファイル / ${bytes.toLocaleString()}バイト`;
}

type ExecutionStatus = Pick<AsynchronousExecutionResponse,
  "executionKind" | "executionState" | "startedAt" | "runAfter" | "reasonCode" | "attachmentTransferProgress">;

/** Use the current root's formal state; a schedule alone does not identify why it is waiting. */
export function AsynchronousExecutionStatusGuidance({ execution }: { execution: ExecutionStatus }) {
  const action = alignmentRecoveryAction(execution);
  const waiting = execution.executionKind === "attachment_storage_alignment" && execution.executionState === "queued" &&
    (execution.reasonCode === "alignment_capacity_wait" || execution.reasonCode === "alignment_start_deadline_wait" ||
      execution.reasonCode === "alignment_connection_wait");
  return <>
    {execution.executionKind === "attachment_storage_alignment" && execution.attachmentTransferProgress !== null
      ? attachmentTransferProgressText(execution.attachmentTransferProgress) === "未確認"
        ? <p>転送件数・容量は未確認です。状態を再取得してください。</p>
        : <p>転送確定：{attachmentTransferProgressText(execution.attachmentTransferProgress)}。レコードの処理件数とは別に集計しています。</p>
      : null}
    {execution.executionState === "queued" ? execution.runAfter
      ? <p>{execution.startedAt ? "現在のジョブの再開可能時刻" : "現在のジョブの実行可能時刻"}：{formatDateTime(execution.runAfter)}。時刻を過ぎた後、ワーカーの巡回で処理します。待機中も未完了のジョブです。</p>
      : <p>現在のジョブの実行・再開可能時刻は未確認です。状態を再取得してください。</p>
      : null}
    {waiting ? <p>同じジョブを自動で継続します。新しいジョブの作成や手動の再開操作は不要です。</p> : null}
    {action ? <p>{action}</p> : null}
  </>;
}

function alignmentRecoveryAction(execution: ExecutionStatus): string | null {
  if (execution.executionKind !== "attachment_storage_alignment") return null;
  if (execution.executionState === "failed" || execution.executionState === "canceled") {
    return "このジョブは終了しています。成功済みの更新は元に戻りません。未収束の処理がある場合は、実行詳細で状態と復旧方法を確認してください。";
  }
  if (execution.executionState !== "blocked") return null;
  switch (execution.reasonCode) {
    case "dependency_unavailable":
      return "依存先への接続を確認し、復旧後に同じジョブを再開してください。";
    case "authorization_revoked":
      return "kintoneとストレージの資格情報・権限を確認し、修正後に同じジョブを再開してください。";
    case "pf_response_unknown":
      return "処理結果が未確認です。接続と実行詳細を確認し、同じジョブで結果を回収してください。新しいジョブで転送をやり直さないでください。";
    case "manifest_missing":
    case "manifest_digest_mismatch":
    case "manifest_expired_rescan_required":
      return "処理記録を確認できません。実行詳細で復旧に必要な情報を確認してください。状態を確認せずに転送をやり直さないでください。";
    case "pf_identity_conflict":
      return "処理の対象または操作IDを実行詳細で確認し、必要な復旧を行ってください。";
    case "source_precondition_changed":
      return "現在のレコード・添付・設定を確認し、阻害要因を解消してから同じジョブを再開してください。";
    case "manual_repair_required":
      return "実行詳細の理由と添付状態を確認し、阻害要因を解消してから同じジョブを再開してください。";
    default:
      return null;
  }
}

export function alignmentCommonFailureGuidance(execution: Pick<AsynchronousExecutionResponse, "alignmentOrigin" | "executionState" | "commonFailureCount">) {
  if (execution.executionState === "canceled") return "このジョブは取り消されています。自動再試行しません。";
  if (execution.executionState === "running") return "共通障害後の再試行を実行しています。";
  if (execution.alignmentOrigin === "automatic") return execution.commonFailureCount === 6
    ? "共通障害の再試行上限に達し、自動整合化を一時停止しています。設定・接続を確認してください。保存先への書込み成功後に再開します。"
    : "初回は10分後、その後は3時間間隔で最大4回再試行します。";
  return (execution.commonFailureCount ?? 0) >= 2
    ? "10分後の再試行も失敗したため停止しています。原因を解消して明示的に再開してください。"
    : "10分後に1回だけ再試行します。再試行も失敗した場合は原因を解消して明示的に再開してください。";
}

export function attachmentStorageAlignmentFailureLabel(
  failureCode: string,
) {
  if (failureCode === "attachment_storage_alignment_source_size_invalid") {
    return "添付ファイルのサイズを取得できません。kintoneの添付情報を確認し、整合化を再開してください。";
  }
  if (failureCode === "attachment_storage_alignment_source_size_exceeds_budget") {
    return "1ファイルが転送上限の1GiBを超えています。添付を分割・差し替えた後に整合化を再開してください。";
  }
  return `内部エラーコード: ${failureCode}`;
}

export function AsynchronousExecutionStatePill({
  state,
  startedAt,
}: {
  state: AsynchronousExecutionState;
  startedAt: string | null | undefined;
}) {
  const completed = state === "completed";
  const attention = state === "blocked" || state === "failed";
  return (
    <span
      className={[
        "inline-flex items-center gap-1 rounded-full border px-2 py-1 text-xs font-medium",
        completed
          ? "border-emerald-300 bg-emerald-50 text-emerald-900"
          : attention
            ? "border-amber-300 bg-amber-50 text-amber-900"
            : "border-sky-300 bg-sky-50 text-sky-900",
      ].join(" ")}
    >
      {completed ? <CheckCircle2 className="h-3.5 w-3.5" /> : null}
      {attention ? <OctagonAlert className="h-3.5 w-3.5" /> : null}
      {!completed && !attention ? <Clock3 className="h-3.5 w-3.5" /> : null}
      {asynchronousExecutionStateLabel(state, startedAt)}
    </span>
  );
}
